import { getFlightItems } from 'checkout/selectors/view/flights'
import { getBedbankItems, getLeHotelItems } from 'checkout/selectors/view/hotels'
import { getVillaItems } from 'checkout/selectors/view/villa'
import { MultipleRoomPackage, OfferPageState, SelectedMultipleRoomPackage } from 'contexts/OfferPage/offerPageStateReducer'
import { arrayToObject } from 'lib/array/arrayUtils'
import { generateRoomOccupants } from 'lib/offer/occupancyUtils'
import { isBundleOffer, isLEHotel } from 'lib/offer/offerTypes'
import { getBedbankAndFlightsChildMaxAge, getOfferAndFlightsMaxChildAge, getPackageUniqueKey } from 'lib/offer/offerUtils'
import { decodeSearchParams } from 'lib/search/searchUtils'
import { createSelector } from 'reselect'
import moment from 'moment'
import { getPromotionMinLos } from 'selectors/offerPage/bedbankOfferPageSelectors'
import { multiRoomEnabled } from '../featuresSelectors'
import { getDefaultAirport } from '../flightsSelectors'
import { isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import uuidV4 from 'lib/string/uuidV4Utils'
import { getLowestPricePackage } from 'lib/offer/getLowestPricePackage'

const getInitialLEHotelStateFromCheckout = createSelector(
  (state: App.State) => getLeHotelItems(state),
  (state: App.State) => getVillaItems(state),
  getFlightItems,
  (_state: App.State, offer: App.Offer) => offer,
  (state: App.State, offer: App.Offer) => isLEHotel(offer) && multiRoomEnabled(state, offer),
  (state: App.State) => state.checkout.cart.connectedRoomOfferIds,
  (accommodationItems, villaItems, flightItems, offer, isMultiRoomEnabled, connectedRoomOfferIds): Partial<OfferPageState> => {
    const items = [
      ...accommodationItems.filter(isInstantBookingLEHotelItem),
      ...villaItems,
    ].filter(item => item.offerId === offer.id)

    if (items.length === 0) { return {} }

    const packagesByKey = arrayToObject(offer.packages, pkg => getPackageUniqueKey(pkg.id, pkg.duration, pkg.roomRate!.id))
    const allItemsWithBundledFlights = new Set(flightItems.flatMap(item => item.bundledItemIds ?? []))

    const [rooms, multipleRoomPackages] = items.reduce<[Array<App.RoomOccupants>, Array<MultipleRoomPackage>]>(
      (accumulator, item) => {
        const pkg = packagesByKey[getPackageUniqueKey(item.packageId, item.duration, item.roomRateId)]
        if (!pkg) {
          return accumulator
        }

        if (isMultiRoomEnabled) {
          // make sure our room ids match on the package and the room
          if (pkg.roomRate) {
            const roomId = uuidV4()
            const multiRoomPackage: SelectedMultipleRoomPackage = {
              roomId,
              roomTypeId: pkg.roomType!.id,
              isFlightsSelected: allItemsWithBundledFlights.has(item.itemId),
              packageId: pkg.id,
              roomRateId: pkg.roomRate?.id,
            }

            const room = generateRoomOccupants({
              ...item.occupancy,
              roomId,
            })

            accumulator[0].push(room)
            accumulator[1].push(multiRoomPackage)
          }
          return accumulator
        } else {
          // no multi room, only need rooms
          accumulator[0].push(generateRoomOccupants(item.occupancy))
          return accumulator
        }
      }, [[], []])

    return {
      checkInDate: items[0].checkIn,
      checkOutDate: items[0].checkOut,
      duration: items[0].duration,
      durationSelected: true,
      rooms,
      multipleRoomPackages,
      isMultiRoomConnected: !!connectedRoomOfferIds?.length,
    }
  },
)

const getInitialLEHotelState = createSelector(
  getInitialLEHotelStateFromCheckout,
  (_state: App.State, offer: App.Offer) => offer,
  (accommodationState, offer): Partial<OfferPageState> => {
    return {
      duration: offer.lowestPricePackage?.duration,
      ...accommodationState,
    }
  },
)

const getInitialBedbankState = createSelector(
  (state: App.State) => getBedbankItems(state),
  (items): Partial<OfferPageState> => {
    if (items.length === 0) {
      return {
        // Certain parts of the bedbank offer page assume `rooms` is always defined,
        // even though it's typed as optional. So the initial state needs to include it.
        rooms: [generateRoomOccupants()],
      }
    }
    const referenceItem = items[0]
    const rooms = items.map(item => generateRoomOccupants(item.occupancy))

    return {
      ...('checkIn' in referenceItem && { checkInDate: referenceItem.checkIn }),
      ...('checkOut' in referenceItem && { checkOutDate: referenceItem.checkOut }),
      rooms,
    }
  },
)

const getInitialFlightState = createSelector(
  (state: App.State) => getFlightItems(state),
  (state: App.State) => state.geo.originAirports,
  (state: App.State) => getDefaultAirport(state),
  (state: App.State) => state.flights.journeys,
  (items, airportList, defaultAirport, journeys): Partial<OfferPageState> => {
    // Assumption: Only one unique flight
    if (items.length === 0) { return { airport: defaultAirport } }
    const referenceItem = items[0]
    const departureJourney = journeys.find(journey => journey.id === referenceItem.searchId)
    if (!departureJourney) return { airport: defaultAirport }

    const departureAirport = departureJourney.departing.flights[0].departingAirport
    return {
      airport: airportList.find(airport => airport.code === departureAirport) ?? defaultAirport,
    }
  },
)

const getInitialSearchState = createSelector(
  (state: App.State) => state.router.location.search,
  (_state: App.State, offer: App.Offer | App.BedbankOffer | App.BundleOffer) => offer,
  (rawSearchParams, offer) => {
    const args = isLEHotel(offer) || isBundleOffer(offer) ?
        [getOfferAndFlightsMaxChildAge(offer as App.Offer), (offer as App.HotelOffer).property.maxInfantAge] :
        [getBedbankAndFlightsChildMaxAge(offer as App.BedbankOffer)]

    const searchParams = decodeSearchParams(
      rawSearchParams,
      ...args,
    )

    const result: Partial<OfferPageState> = {
      ...(searchParams.rooms.length > 0 && { rooms: searchParams.rooms.map(generateRoomOccupants) }),
    }

    if (searchParams.dates.checkIn) {
      const checkInDate = searchParams.dates.checkIn
      const checkInMoment = moment(checkInDate)
      let availableCheckInDate = true
      if (isLEHotel(offer) && offer.bookByDate && offer.travelToDate) {
        // Must be booking before/on the book by date
        const bookByDate = moment(offer.bookByDate)
        const travelToDate = moment.utc(offer.travelToDate)
        if (moment().isSameOrAfter(bookByDate.add(1, 'day')) || checkInMoment.isSameOrAfter(travelToDate)) {
          availableCheckInDate = false
        }
      }

      if (availableCheckInDate) {
        result.checkInDate = checkInDate

        if (searchParams.dates.checkOut) {
          result.checkOutDate = searchParams.dates.checkOut
          const checkOutMoment = moment(result.checkOutDate)
          result.duration = Math.round(moment.duration(checkOutMoment.diff(result.checkInDate)).asDays())
          result.durationSelected = true
        }
      }
    }

    if (searchParams.bundledOfferId && searchParams.dates.checkIn && searchParams.dates.checkOut) {
      result.bundledOffers = {
        [searchParams.bundledOfferId]: {
          checkInDate: result.checkInDate,
          checkOutDate: result.checkOutDate,
          duration: result.duration,
        },
      }
    }

    if (searchParams.duration && !result.duration) {
      result.duration = searchParams.duration
    }

    return result
  },
)

export const getInitialLeOfferPageState = createSelector(
  (state: App.State, offer: App.Offer) => getInitialLEHotelState(state, offer),
  (state: App.State, offer: App.Offer) => getInitialSearchState(state, offer),
  (state: App.State) => getInitialFlightState(state),
  (accommodationState, searchState, flightState): Partial<OfferPageState> => {
    // The order of the spread elements matter!
    return {
      ...accommodationState,
      ...searchState,
      ...flightState,
    }
  },
)

export const getInitialBedbankOfferPageState = createSelector(
  (state: App.State, _offer: App.BedbankOffer) => getInitialBedbankState(state),
  (state: App.State, offer: App.BedbankOffer) => getInitialSearchState(state, offer),
  (_state: App.State, offer: App.BedbankOffer) => getPromotionMinLos(offer),
  (state: App.State) => getInitialFlightState(state),
  (bedbankState, searchState, promotionMinLos, flightState) => {
    // The order of the spread elements matter! It certainly does!
    return {
      ...bedbankState,
      ...searchState,
      ...flightState,
      duration: searchState.duration ? searchState.duration : promotionMinLos,
    }
  },
)

export const getInitialBundleAndSaveOfferPageState = createSelector(
  (_state: App.State, offer: App.BundleOffer) => offer,
  (state: App.State, offer: App.BundleOffer) => getInitialSearchState(state, offer),
  (offer, searchState): Partial<OfferPageState> => {
    const bundledOffers = Object.values(offer.bundledOffers)
    const lowestPriceBundlePackage = offer.lowestPriceBundlePackage!

    const allOffersIds = Object.keys(offer.bundledOffers)

    const currentOfferId = allOffersIds[0]

    return {
      paginator: {
        currentOfferId,
        nextOfferId: allOffersIds[allOffersIds.indexOf(currentOfferId) + 1],
        backOfferId: allOffersIds[allOffersIds.indexOf(currentOfferId) - 1],
      },
      duration: lowestPriceBundlePackage.duration,
      bundleOffer: isBundleOffer(offer) ? offer : undefined,
      offer,
      bundledOffers: arrayToObject(bundledOffers,
        bundleOffer => bundleOffer.id,
        bundleOffer => {
          const bundlePackages = offer.packages.filter((p) => p.offerId === bundleOffer.id)
          const pkg = lowestPriceBundlePackage.packages.find((pkg) => pkg.offerId === bundleOffer.id)!
          const packages = bundlePackages.length ? bundlePackages : [pkg]
          const attachedOffer: App.HotelOffer = {
            ...offer,
            ...bundleOffer,
            packages,
            lowestPricePackage: getLowestPricePackage(offer, packages),
            loyalty: {
              hasUpgradablePackages: false,
            },
          }

          return {
            offer: attachedOffer,
            duration: pkg.duration,
          }
        },
      ),
      rooms: searchState.rooms?.length ? [searchState.rooms[0]] : undefined,
    }
  },
)
